










































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import PortalSelect from '@/components/new_style_components/PortalSelect.vue';
import PortalInput from '@/components/new_style_components/PortalInput.vue';
import PortalDate from '@/components/new_style_components/PortalDate.vue';
import SelectOption, { OptionsBuilder } from '@/components/common/SelectOption';
import { ChargeComponent } from '@/dto/subscription/SublimeChargeDTO';
import { BillingMode, Periodicity } from '@/dto/subscription/SubscriptionPlanDTO';
import CreatePlanPayloadDTO, { CreatePlanComponentPayloadDTO } from '@/dto/subscription/CreatePlanPayloadDTO';
import SubscriptionPlanService from '@/services/SubscriptionPlanService';
import Notifications from '@/state/Notifications';
import { SublimeCompanyType } from '@/dto/SublimeCompanyType';
import { supportsDefaultIncludedUnits } from '@/utils/SubscriptionPlanComponentCapabilities';
import { getExcludedBy, areMutuallyExclusive } from '@/utils/SubscriptionPlanComponentConstraints';
import { componentsAllowedForIssuer, isComponentAllowedForIssuer } from '@/utils/SubscriptionComponentIssuerAffinity';
import {SubscriberType} from "@/constants/SubscriptionConstants";

interface ComponentFormRow {
  uid: string;
  component: ChargeComponent | null;
  billingMode: BillingMode | null;
  periodicity: Periodicity | null;
  defaultIncludedUnits: number | string | null;
  priceDollars: string;
  effectiveFrom: string;
  effectiveTo: string | null;
}

@Component({
  components: { PortalSelect, PortalInput, PortalDate },
  computed: {
    OptionsBuilder: () => OptionsBuilder,
    SubscriberType: () => SubscriberType
  }
})
export default class AddSubscriptionPlanModal extends Vue {
  @Prop({ required: true }) onCreated!: () => void;

  private issuer: SublimeCompanyType | null = null;
  private name = '';
  private code = '';
  private subscriberType: SubscriberType | null = null;
  private components: Array<ComponentFormRow> = [];
  private submitting = false;

  mounted(): void {
    // Do not pre-populate components until issuer is chosen
  }

  @Watch('issuer')
  private onIssuerChanged(newVal: SublimeCompanyType | null, oldVal: SublimeCompanyType | null): void {
    // If issuer was set and there are no rows, add an empty row for convenience
    if (newVal && (!this.components || this.components.length === 0) && this.canAddComponent) {
      this.addComponent();
    }
    // Clear incompatible selections when issuer changes
    if (newVal !== oldVal) {
      const allowed = componentsAllowedForIssuer(newVal);
      for (const row of this.components) {
        if (row.component && !allowed.has(row.component)) {
          row.component = null;
          // optional: keep other fields; component must be reselected
        }
      }
    }
  }

  get issuerOptions(): Array<SelectOption> {
    return new OptionsBuilder()
      // .of('Sublime Accounting Inc', SublimeCompanyType.SUBLIME_ACCOUNTING)
      .of('Sublime Payroll Services Inc', SublimeCompanyType.SUBLIME_PAYROLL_SERVICES)
      .of('Sublime Consulting Inc', SublimeCompanyType.SUBLIME_CONSULTING)
      // .of('Sublime Projects Inc', SublimeCompanyType.SUBLIME_PROJECTS)
      .get();
  }

  // Returns all components (unfiltered). Kept for potential reuse
  get componentOptions(): Array<SelectOption> {
    return Object.values(ChargeComponent).map(v => ({ title: this.$stringUtils.enumToFormattedSentence(v), value: v } as SelectOption));
  }

  get billingModeOptions(): Array<SelectOption> {
    return Object.values(BillingMode).map(v => ({ title: this.$stringUtils.enumToFormattedSentence(v), value: v } as SelectOption));
  }

  get periodicityOptions(): Array<SelectOption> {
    return Object.values(Periodicity).map(v => ({ title: this.$stringUtils.enumToFormattedSentence(v), value: v } as SelectOption));
  }

  private componentOptionsFor(row: ComponentFormRow): Array<SelectOption> {
    // If issuer is not selected yet, no components should be presented
    if (!this.issuer) return [];
    const allowed = componentsAllowedForIssuer(this.issuer);
    const otherSelected = (this.components || []).filter(r => r !== row && !!r.component).map(r => r.component as ChargeComponent);
    const excluded = getExcludedBy(otherSelected);
    const taken = new Set(otherSelected as any[]);
    return Object.values(ChargeComponent)
      .filter(v => allowed.has(v as ChargeComponent))
      .filter(v => v === row.component || (!taken.has(v as any) && !excluded.has(v as ChargeComponent)))
      .map(v => ({ title: this.$stringUtils.enumToFormattedSentence(v), value: v } as SelectOption));
  }

  get canAddComponent(): boolean {
    if (!this.issuer) return false;
    const allowed = componentsAllowedForIssuer(this.issuer);
    const selected = (this.components || []).filter(c => !!c.component).map(c => c.component as ChargeComponent);
    const selectedSet = new Set(selected as any[]);
    const excluded = getExcludedBy(selected);
    const available = Array.from(allowed).filter(v => !selectedSet.has(v as any) && !excluded.has(v as ChargeComponent));
    return available.length > 0;
  }

  private addComponent(): void {
    if (!this.issuer) {
      Notifications.warning('Please select Issuer first');
      return;
    }
    if (!this.canAddComponent) {
      Notifications.warning('No more compatible components can be added');
      return;
    }
    this.components.push({
      uid: Math.random().toString(36).slice(2),
      component: null,
      billingMode: null,
      periodicity: null,
      defaultIncludedUnits: null,
      priceDollars: '',
      effectiveFrom: '',
      effectiveTo: null,
    });
  }

  private removeComponent(index: number): void {
    this.components.splice(index, 1);
  }

  private canHaveDefaultUnits(comp: ChargeComponent | null): boolean {
    return supportsDefaultIncludedUnits(comp as any);
  }

  private onComponentChange(row: ComponentFormRow): void {
    if (!this.canHaveDefaultUnits(row.component)) {
      row.defaultIncludedUnits = null;
    }
  }

  private submit(): void {
    if (!this.issuer || !this.name || !this.code) {
      Notifications.warning('Please fill Issuer, Name and Code');
      return;
    }
    if (!this.components.length) {
      Notifications.warning('Please add at least one component');
      return;
    }
    const compPayloads: Array<CreatePlanComponentPayloadDTO> = [];
    // Ensure no duplicate components
    const comps = this.components.map(c => c.component).filter(Boolean) as Array<ChargeComponent>;
    if (new Set(comps).size !== comps.length) {
      Notifications.warning('Duplicate components are not allowed');
      return;
    }
    // Ensure no mutually exclusive components are selected together
    for (let i = 0; i < comps.length; i++) {
      for (let j = i + 1; j < comps.length; j++) {
        if (areMutuallyExclusive(comps[i], comps[j])) {
          const a = this.$stringUtils.enumToFormattedSentence(comps[i]);
          const b = this.$stringUtils.enumToFormattedSentence(comps[j]);
          Notifications.warning(`Components ${a} and ${b} cannot be used together`);
          return;
        }
      }
    }
    for (const c of this.components) {
      if (!c.component || !c.billingMode || !c.periodicity || !c.priceDollars || !c.effectiveFrom) {
        Notifications.warning('Please complete all component fields');
        return;
      }
      const price = Number(c.priceDollars);
      if (Number.isNaN(price) || price < 0) {
        Notifications.warning('Please provide valid unit price');
        return;
      }
      compPayloads.push({
        component: c.component,
        billingMode: c.billingMode,
        periodicity: c.periodicity,
        defaultIncludedUnits: this.canHaveDefaultUnits(c.component)
          ? (c.defaultIncludedUnits !== null && c.defaultIncludedUnits !== undefined && c.defaultIncludedUnits !== ''
              ? Number(c.defaultIncludedUnits)
              : undefined)
          : undefined,
        price: {
          unitPriceCents: Math.round(price * 100),
          effectiveFrom: c.effectiveFrom,
          effectiveTo: c.effectiveTo || null,
        }
      });
    }

    const payload: CreatePlanPayloadDTO = {
      issuer: this.issuer,
      name: this.name,
      subscriberType: this.subscriberType,
      code: this.code,
      isPublic: true,
      archivePreviousActive: true,
      components: compPayloads,
    };

    this.submitting = true;
    this.$app.startLoading();
    SubscriptionPlanService.createPlan(payload).then(
      () => {
        this.$app.stopLoading();
        this.submitting = false;
        Notifications.success('Plan created');
        this.onCreated();
      },
      err => {
        this.$app.stopLoading();
        this.submitting = false;
        Notifications.error('Failed to create plan');
      }
    )
  }

  private cancel(): void {
    this.$modal.hideAll();
  }
}
